import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  ShieldCheck,
  UserCircle2,
  Activity,
  Network,
  Globe,
  AlertTriangle,
  Mail,
  BrainCircuit,
  Cloud,
  Server,
  ClipboardList,
  Headphones,
  LogOut,
  KeyRound,
  Settings,
  Plug,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { allowedModules, ROLE_LABELS } from "../lib/modules";
import OrgSwitcher from "./OrgSwitcher";

const NAV_ITEMS = [
  { to: "/", key: "overview", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/assets", key: "assets", label: "Asset Management", icon: Server, testid: "nav-assets" },
  { to: "/control", key: "control", label: "Control Assurance", icon: ShieldCheck, testid: "nav-control" },
  { to: "/cloud", key: "cloud", label: "Cloud Security", icon: Cloud, testid: "nav-cloud" },
  { to: "/identity", key: "identity", label: "Identity Health", icon: UserCircle2, testid: "nav-identity" },
  { to: "/soc", key: "soc", label: "SOC Operations", icon: Activity, testid: "nav-soc" },
  { to: "/firewall", key: "firewall", label: "Firewall Health", icon: Network, testid: "nav-firewall" },
  { to: "/webgw", key: "webgw", label: "Web Gateway Health", icon: Globe, testid: "nav-webgw" },
  { to: "/vuln", key: "vuln", label: "Vulnerability Mgmt", icon: AlertTriangle, testid: "nav-vuln" },
  { to: "/email", key: "email", label: "Email Security", icon: Mail, testid: "nav-email" },
  { to: "/aisec", key: "aisec", label: "AI Security", icon: BrainCircuit, testid: "nav-aisec" },
  { to: "/projects", key: "projects", label: "Project Management", icon: ClipboardList, testid: "nav-projects" },
  { to: "/support", key: "support", label: "Support Effectiveness", icon: Headphones, testid: "nav-support" },
];

export default function Sidebar() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const allowed = allowedModules(user);
  const visibleNav = NAV_ITEMS.filter((n) => allowed.includes(n.key));
  const isAdmin = user && (user.role === "super_admin" || user.role === "org_admin");
  return (
    <aside
      data-testid="sidebar"
      className="w-[230px] shrink-0 sticky top-0 h-screen flex flex-col relative"
      style={{
        background: "#ffffff",
        borderRight: "1px solid var(--cv-line)",
        color: "var(--cv-charcoal)",
      }}
    >
      {/* thin brand accent stripe on the right edge */}
      <div
        aria-hidden
        className="absolute top-0 bottom-0 right-0 w-[2px]"
        style={{
          background:
            "linear-gradient(180deg, var(--cv-crimson) 0%, var(--cv-orange) 45%, var(--cv-gold) 100%)",
          opacity: 0.55,
        }}
      />

      <div
        className="px-[22px] py-5"
        style={{ borderBottom: "1px solid var(--cv-line)" }}
      >
        <div
          className="text-[23px] font-bold small-caps leading-none"
          style={{
            fontFamily: "Cambria, Times New Roman, Georgia, serif",
            color: "var(--cv-charcoal)",
          }}
        >
          Cyber<span style={{ color: "var(--cv-crimson)" }}>Visible</span>
        </div>
        <div
          className="text-[10.5px] tracking-[0.22em] uppercase mt-1.5 font-bold"
          style={{ color: "var(--cv-dark-grey)" }}
        >
          CyberNxt Solutions
        </div>
      </div>

      <OrgSwitcher user={user} />

      <nav className="flex-1 overflow-y-auto py-[14px]">
        <div
          className="text-[10.5px] small-caps font-bold px-[22px] py-1.5 tracking-[0.22em]"
          style={{ color: "var(--cv-dark-grey)" }}
        >
          Modules
        </div>
        {visibleNav.map((n) => {
          const Icon = n.icon;
          const active = loc.pathname === n.to || (n.to !== "/" && loc.pathname.startsWith(n.to));
          return (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className="flex items-center gap-[11px] w-full text-[14.5px] px-[22px] py-[11px] transition-colors"
              style={{
                color: active ? "var(--cv-crimson)" : "var(--cv-charcoal)",
                background: active
                  ? "linear-gradient(90deg, rgba(165,48,15,0.10) 0%, rgba(165,48,15,0.02) 100%)"
                  : "transparent",
                borderLeft: active
                  ? "3px solid var(--cv-crimson)"
                  : "3px solid transparent",
                fontWeight: active ? 700 : 500,
              }}
            >
              <Icon
                className="w-[18px] h-[18px]"
                style={{ color: active ? "var(--cv-crimson)" : "var(--cv-dark-grey)" }}
              />
              <span>{n.label}</span>
            </NavLink>
          );
        })}

        {isAdmin && (
          <>
            <div
              className="text-[10.5px] small-caps font-bold px-[22px] py-1.5 mt-2 tracking-[0.22em]"
              style={{ color: "var(--cv-dark-grey)" }}
            >
              Administration
            </div>
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className="flex items-center gap-[11px] w-full text-[14.5px] px-[22px] py-[11px] transition-colors"
              style={{
                color: loc.pathname.startsWith("/admin") ? "var(--cv-crimson)" : "var(--cv-charcoal)",
                background: loc.pathname.startsWith("/admin")
                  ? "linear-gradient(90deg, rgba(165,48,15,0.10) 0%, rgba(165,48,15,0.02) 100%)"
                  : "transparent",
                borderLeft: loc.pathname.startsWith("/admin")
                  ? "3px solid var(--cv-crimson)"
                  : "3px solid transparent",
                fontWeight: loc.pathname.startsWith("/admin") ? 700 : 500,
              }}
            >
              <Settings
                className="w-[18px] h-[18px]"
                style={{ color: loc.pathname.startsWith("/admin") ? "var(--cv-crimson)" : "var(--cv-dark-grey)" }}
              />
              <span>Admin Panel</span>
            </NavLink>
            <NavLink
              to="/connectors"
              data-testid="nav-connectors"
              className="flex items-center gap-[11px] w-full text-[14.5px] px-[22px] py-[11px] transition-colors"
              style={{
                color: loc.pathname.startsWith("/connectors") ? "var(--cv-crimson)" : "var(--cv-charcoal)",
                background: loc.pathname.startsWith("/connectors")
                  ? "linear-gradient(90deg, rgba(165,48,15,0.10) 0%, rgba(165,48,15,0.02) 100%)"
                  : "transparent",
                borderLeft: loc.pathname.startsWith("/connectors")
                  ? "3px solid var(--cv-crimson)"
                  : "3px solid transparent",
                fontWeight: loc.pathname.startsWith("/connectors") ? 700 : 500,
              }}
            >
              <Plug
                className="w-[18px] h-[18px]"
                style={{ color: loc.pathname.startsWith("/connectors") ? "var(--cv-crimson)" : "var(--cv-dark-grey)" }}
              />
              <span>Connectors</span>
            </NavLink>
          </>
        )}
      </nav>

      <div
        className="px-[20px] py-4"
        style={{
          borderTop: "1px solid var(--cv-line)",
          background: "var(--cv-sand)",
        }}
        data-testid="sidebar-user"
      >
        {user && (
          <>
            <div
              className="text-[13px] font-bold leading-tight"
              style={{ color: "var(--cv-charcoal)" }}
            >
              {user.name || user.email}
            </div>
            <div
              className="text-[10.5px] mt-0.5 lowercase truncate"
              style={{ color: "var(--cv-dark-grey)" }}
            >
              {user.email}
            </div>
            <div className="flex items-center gap-2 mt-2 text-[10.5px]">
              <span
                className="status-pill"
                style={{
                  background: "rgba(213,88,22,0.14)",
                  color: "var(--cv-orange)",
                  border: "1px solid rgba(213,88,22,0.28)",
                }}
              >
                {ROLE_LABELS[user.role] || user.role || "user"}
              </span>
              <span
                className={`status-pill ${user.totp_enrolled ? "status-compliant" : "status-non-compliant"}`}
              >
                {user.totp_enrolled ? "2FA on" : "2FA off"}
              </span>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                data-testid="sidebar-2fa"
                onClick={() => nav("/security/2fa")}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-[11px] small-caps font-bold"
                style={{
                  background: "#fff",
                  color: "var(--cv-charcoal)",
                  border: "1px solid var(--cv-line)",
                }}
              >
                <KeyRound className="w-3.5 h-3.5" /> 2FA
              </button>
              <button
                data-testid="sidebar-logout"
                onClick={logout}
                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-[11px] small-caps font-bold"
                style={{
                  background:
                    "linear-gradient(180deg, #C13818 0%, #A5300F 100%)",
                  color: "#fff",
                  boxShadow: "0 4px 10px rgba(165,48,15,0.24)",
                }}
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
